import { execSync } from "child_process";
import { uploadLiveLog } from "@saas/components/src/utils/log";

/**
 * 系统音量控制工具
 * 支持 macOS 和 Windows 平台，通过原生命令直接控制系统音量
 * 用于解决共享系统音频时 TRTC SDK 无法准确获取/设置系统音量的问题
 */

/**
 * 获取系统扬声器音量（跨平台）
 * @returns {number} 音量值 0-100，失败返回 null
 */
export function getSystemSpeakerVolume() {
  if (process.platform === "darwin") {
    // macOS 使用 osascript 读取系统音量
    try {
      const volume = execSync(
        'osascript -e "output volume of (get volume settings)"',
        {
          encoding: "utf8",
          timeout: 1000,
        }
      ).trim();
      const volumeInt = parseInt(volume, 10);
      uploadLiveLog({
        message: "获取系统音量（原生API）",
        level: "info",
        tag: "api",
        extra: {
          component: "SystemVolumeUtils",
          volume: volumeInt,
          method: "osascript",
          platform: "macOS",
        },
      });
      return volumeInt;
    } catch (error) {
      console.error("获取 macOS 系统音量失败:", error);
      uploadLiveLog({
        message: "获取系统音量失败",
        level: "warn",
        tag: "api",
        extra: {
          component: "SystemVolumeUtils",
          error: error.message,
          platform: "macOS",
        },
      });
      return null;
    }
  } else if (process.platform === "win32") {
    // Windows 使用 PowerShell 读取系统音量
    try {
      // 使用 PowerShell 脚本获取主音频设备音量
      const psScript = `
        Add-Type -TypeDefinition @"
          using System;
          using System.Runtime.InteropServices;
          [Guid, ComImport, InterfaceType(ComInterfaceType.InterfaceIsIUnknown)]
          [Guid("5CDF2C82-841E-4546-9722-0CF74078229A")]
          public interface IAudioEndpointVolume {
            int NotImpl1(); int NotImpl2();
            int GetMasterVolumeLevelScalar(out float level);
            int SetMasterVolumeLevelScalar(float level, IntPtr guid);
          }
          [Guid, ComImport, InterfaceType(ComInterfaceType.InterfaceIsIUnknown)]
          [Guid("D666063F-1587-4E43-81F1-B948E807363F")]
          public interface IMMDevice {
            int Activate(ref Guid id, int clsCtx, IntPtr activationParams, out IAudioEndpointVolume aev);
          }
          [Guid, ComImport, InterfaceType(ComInterfaceType.InterfaceIsIUnknown)]
          [Guid("A95664D2-9614-4F35-A746-DE8DB63617E6")]
          public interface IMMDeviceEnumerator {
            int NotImpl1();
            int GetDefaultAudioEndpoint(int dataFlow, int role, out IMMDevice device);
          }
          [ComImport, Guid("BCDE0395-E52F-467C-8E3D-C4579291692E")]
          public class MMDeviceEnumeratorCom { }
          public class Audio {
            public static float GetVolume() {
              var enumerator = new MMDeviceEnumeratorCom() as IMMDeviceEnumerator;
              IMMDevice dev; enumerator.GetDefaultAudioEndpoint(0, 1, out dev);
              var id = typeof(IAudioEndpointVolume).GUID;
              IAudioEndpointVolume aev; dev.Activate(ref id, 0, IntPtr.Zero, out aev);
              float volume; aev.GetMasterVolumeLevelScalar(out volume);
              return volume;
            }
          }
"@
        [Math]::Round([Audio]::GetVolume() * 100)
      `.trim();

      const volume = execSync(`powershell -Command "${psScript}"`, {
        encoding: "utf8",
        timeout: 2000,
      }).trim();

      const volumeInt = parseInt(volume, 10);
      uploadLiveLog({
        message: "获取系统音量（原生API）",
        level: "info",
        tag: "api",
        extra: {
          component: "SystemVolumeUtils",
          volume: volumeInt,
          method: "powershell",
          platform: "Windows",
        },
      });
      return volumeInt;
    } catch (error) {
      console.error("获取 Windows 系统音量失败:", error);
      uploadLiveLog({
        message: "获取系统音量失败",
        level: "warn",
        tag: "api",
        extra: {
          component: "SystemVolumeUtils",
          error: error.message,
          platform: "Windows",
        },
      });
      return null;
    }
  }
  
  // 其他平台不支持
  console.warn("当前平台不支持系统音量控制:", process.platform);
  return null;
}

/**
 * 设置系统扬声器音量（跨平台）
 * @param {number} volume - 音量值 0-100
 * @returns {boolean} 是否设置成功
 */
export function setSystemSpeakerVolume(volume) {
  if (process.platform === "darwin") {
    // macOS 使用 osascript 设置系统音量
    try {
      execSync(`osascript -e "set volume output volume ${volume}"`, {
        encoding: "utf8",
        timeout: 1000,
      });
      uploadLiveLog({
        message: "设置系统音量（原生API）",
        level: "info",
        tag: "api",
        extra: {
          component: "SystemVolumeUtils",
          volume,
          method: "osascript",
          platform: "macOS",
        },
      });
      return true;
    } catch (error) {
      console.error("设置 macOS 系统音量失败:", error);
      uploadLiveLog({
        message: "设置系统音量失败",
        level: "warn",
        tag: "api",
        extra: {
          component: "SystemVolumeUtils",
          error: error.message,
          volume,
          platform: "macOS",
        },
      });
      return false;
    }
  } else if (process.platform === "win32") {
    // Windows 使用 PowerShell 设置系统音量
    try {
      // 将音量转换为 0-1 的浮点数
      const volumeFloat = (volume / 100).toFixed(2);

      // 使用 PowerShell 脚本设置主音频设备音量
      const psScript = `
        Add-Type -TypeDefinition @"
          using System;
          using System.Runtime.InteropServices;
          [Guid, ComImport, InterfaceType(ComInterfaceType.InterfaceIsIUnknown)]
          [Guid("5CDF2C82-841E-4546-9722-0CF74078229A")]
          public interface IAudioEndpointVolume {
            int NotImpl1(); int NotImpl2();
            int GetMasterVolumeLevelScalar(out float level);
            int SetMasterVolumeLevelScalar(float level, IntPtr guid);
          }
          [Guid, ComImport, InterfaceType(ComInterfaceType.InterfaceIsIUnknown)]
          [Guid("D666063F-1587-4E43-81F1-B948E807363F")]
          public interface IMMDevice {
            int Activate(ref Guid id, int clsCtx, IntPtr activationParams, out IAudioEndpointVolume aev);
          }
          [Guid, ComImport, InterfaceType(ComInterfaceType.InterfaceIsIUnknown)]
          [Guid("A95664D2-9614-4F35-A746-DE8DB63617E6")]
          public interface IMMDeviceEnumerator {
            int NotImpl1();
            int GetDefaultAudioEndpoint(int dataFlow, int role, out IMMDevice device);
          }
          [ComImport, Guid("BCDE0395-E52F-467C-8E3D-C4579291692E")]
          public class MMDeviceEnumeratorCom { }
          public class Audio {
            public static void SetVolume(float level) {
              var enumerator = new MMDeviceEnumeratorCom() as IMMDeviceEnumerator;
              IMMDevice dev; enumerator.GetDefaultAudioEndpoint(0, 1, out dev);
              var id = typeof(IAudioEndpointVolume).GUID;
              IAudioEndpointVolume aev; dev.Activate(ref id, 0, IntPtr.Zero, out aev);
              aev.SetMasterVolumeLevelScalar(level, IntPtr.Zero);
            }
          }
"@
        [Audio]::SetVolume(${volumeFloat})
      `.trim();

      execSync(`powershell -Command "${psScript}"`, {
        encoding: "utf8",
        timeout: 2000,
      });

      uploadLiveLog({
        message: "设置系统音量（原生API）",
        level: "info",
        tag: "api",
        extra: {
          component: "SystemVolumeUtils",
          volume,
          method: "powershell",
          platform: "Windows",
        },
      });
      return true;
    } catch (error) {
      console.error("设置 Windows 系统音量失败:", error);
      uploadLiveLog({
        message: "设置系统音量失败",
        level: "warn",
        tag: "api",
        extra: {
          component: "SystemVolumeUtils",
          error: error.message,
          volume,
          platform: "Windows",
        },
      });
      return false;
    }
  }
  
  // 其他平台不支持
  console.warn("当前平台不支持系统音量控制:", process.platform);
  return false;
}
